import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, Inject, Input } from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
  MatDatepicker,
  MatDatepickerModule,
} from '@angular/material/datepicker';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { CommonService } from '../../../../../../services/common.service';
import { NgxSpinnerService, NgxSpinnerComponent } from 'ngx-spinner';
import { ToastrService } from '@openng/ngx-toastr';
import { MatTableModule } from '@angular/material/table';
import { ContributorCommissionRatePlanService } from '../../../../../../services/contributor-commission-rate-plan.service';
import { resolveDualCommissionRates } from '../../../../models/commission-rate-tier.models';

@Component({
  selector: 'app-view-pending-approvals',
  imports: [
    MatDatepicker,
    MatDatepickerModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatIconModule,
    CommonModule,
    MatDialogModule,
    NgxSpinnerComponent,
    MatTableModule,
  ],
  templateUrl: './view-pending-approvals.component.html',
  styleUrls: ['./view-pending-approvals.component.scss'],
})
export class ViewCommissionRatePlanPendingApprovalsDialogComponent {
  @Input() contributorId: any;
  firstSectionForm: FormGroup;
  secondSectionForm: FormGroup;
  isStrikedOut: boolean = false;
  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<ViewCommissionRatePlanPendingApprovalsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private commonService: CommonService,
    private spinner: NgxSpinnerService,
    private toasterService: ToastrService,
    private cdr: ChangeDetectorRef,
    private commissionRatePlanService: ContributorCommissionRatePlanService,
  ) {
    this.firstSectionForm = this.fb.group({
      templateName: [''],
      contributionPeriodName: [''],
      effectiveDate: [''],
      thresholds: this.fb.array([]),
    });

    this.secondSectionForm = this.fb.group({
      templateName: [''],
      contributionPeriodName: [''],
      effectiveDate: [''],
      thresholds: this.fb.array([]),
    });
  }

  get firstThresholdFormArray(): FormArray {
    return this.firstSectionForm.get('thresholds') as FormArray;
  }

  get secondThresholdFormArray(): FormArray {
    return this.secondSectionForm.get('thresholds') as FormArray;
  }

  private toLocalDate(value: string | null | undefined): Date | null {
    if (!value) {
      return null;
    }

    return this.commonService.parseLocalDate(value);
  }

  private createThresholdFormGroup(threshold: any): FormGroup {
    const resolvedRates = resolveDualCommissionRates(threshold)

    return this.fb.group({
      min: [threshold.ytdThresholdStart ?? threshold.thresholdStart],
      max: [threshold.ytdThresholdEnd ?? threshold.thresholdEnd],
      contractRate: [resolvedRates.contractRatePercentage],
      permRate: [resolvedRates.permRatePercentage],
    })
  }

  ngOnInit(): void {
    this.commissionRatePlanService
      .viewContributorCommissionPendingApprovals(
        this.data.data.contributorId,
        this.data.data.id,
      )
      .subscribe({
        next: (data) => {
          this.data = data;
          if (data.approval.isToBeDeleted) {
            this.isStrikedOut = true;

            this.firstSectionForm.patchValue({
              effectiveDate: this.toLocalDate(data.current.effectiveDate),
              templateName: data.current.name,
              contributionPeriodName: data.current.contributionPeriodName,
            });

            // Update FormArray manually
            const formGroups =
              data.current.contributorCommissionRatePlanTiers.map(
                (threshold: any) => this.createThresholdFormGroup(threshold),
              );

            this.firstSectionForm.setControl(
              'thresholds',
              this.fb.array(formGroups),
            );
          } else {
            // Patch basic values
            this.firstSectionForm.patchValue({
              effectiveDate: this.toLocalDate(data.approval.effectiveDate),
              contributionPeriodName:
                data.approval.contributionPeriodName ?? '',
              templateName: data.approval.name,
            });

            // Update FormArray manually
            const formGroups = data.approval.commissionTemplateTiers.map(
              (threshold: any) => this.createThresholdFormGroup(threshold),
            );

            this.firstSectionForm.setControl(
              'thresholds',
              this.fb.array(formGroups),
            );
          }
          if (data.current) {
            this.secondSectionForm.patchValue({
              effectiveDate: this.toLocalDate(data.current.effectiveDate),
              templateName: data.current.name,
            });

            const secondFormGroups =
              data.current.contributorCommissionRatePlanTiers.map(
                (threshold: any) => this.createThresholdFormGroup(threshold),
              );

            this.secondSectionForm.setControl(
              'thresholds',
              this.fb.array(secondFormGroups),
            );
          }

          this.firstSectionForm.disable();
          this.secondSectionForm.disable();
          this.cdr.detectChanges();
        },
        error: (error) => {
          this.toasterService.error(
            'Failed to load commission rate plan details',
          );
          this.cdr.detectChanges();
        },
      });
  }

  loadThresholds(rateDtos: any[]): void {
    const validRates = rateDtos
      .map((dto) => {
        const resolvedRates = resolveDualCommissionRates(dto)
        return {
          ...dto,
          contractRatePercentage: resolvedRates.contractRatePercentage,
          permRatePercentage: resolvedRates.permRatePercentage,
        }
      })
      .filter(
        (dto) =>
          dto.thresholdStart !== null &&
          dto.thresholdEnd !== null &&
          dto.contractRatePercentage !== null &&
          dto.permRatePercentage !== null &&
          (dto.thresholdStart !== 0 ||
            dto.thresholdEnd !== 0 ||
            dto.contractRatePercentage !== 0 ||
            dto.permRatePercentage !== 0),
      );

    const formGroups = validRates.map((dto) =>
      this.fb.group({
        min: [dto.thresholdStart],
        max: [dto.thresholdEnd],
        contractRate: [dto.contractRatePercentage],
        permRate: [dto.permRatePercentage],
      }),
    );

    this.fb.array(formGroups);
  }

  cancel(): void {
    this.dialogRef.close();
  }
  approve(): void {
    this.commissionRatePlanService
      .bulkApprove([this.data.approval.id])
      .subscribe({
        next: (response) => {
          const failedEntries = Object.entries(response?.failed ?? {});
          const successCount = response?.succeeded?.length ?? 0;

          if (successCount) {
            this.toasterService.success(
              `${successCount} commission rate plan(s) approved successfully`,
              'Approved',
            );
            this.dialogRef.close({
              action: 'approve',
              data: this.firstSectionForm.value,
            });
          }

          if (failedEntries.length) {
            const reasons = failedEntries
              .map(([id, reason]) => `#${id}: ${String(reason)}`)
              .join('\n');
            this.toasterService.error(
              reasons,
              `${failedEntries.length} approval(s) failed`,
              { disableTimeOut: true, enableHtml: false },
            );
          }

          this.cdr.detectChanges();
        },
        error: (error) => {
          this.toasterService.error(
            'Error approving Commission Rate Plan Approval Request',
            'Error',
          );
          this.cdr.detectChanges();
        },
      });
  }
  reject(): void {
    this.commissionRatePlanService
      .rejectPendingCommissionRatePlan(this.data.approval.id)
      .subscribe({
        next: (response) => {
          this.toasterService.success(
            'Commission Rate Plan Approval Request Rejected Successfully',
          );
          this.dialogRef.close();
          this.cdr.detectChanges();
        },
        error: (error) => {
          this.toasterService.error(
            'Error rejecting Commission Rate Plan Approval Request',
            'Error',
          );
          this.cdr.detectChanges();
        },
      });
    this.dialogRef.close({
      action: 'reject',
      data: this.firstSectionForm.value,
    });
  }
}
